/**
 * チャンク逐次アップロード機能
 * 署名付きURLを使用してCloudflare R2に直接アップロード
 */

import { IndexedDBStorage } from './indexedDBStorage.js'

export class ChunkUploader {
  constructor(options = {}) {
    this.apiBaseUrl = options.apiBaseUrl || import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    
    // R2設定（開発環境用）
    this.r2Endpoint = 'https://pub-2b0f3ce6876948b8a8300ffc2709e84c.r2.dev'
    this.r2BucketName = 'otoeigo-recordings'
    this.storage = new IndexedDBStorage()
    this.maxRetries = options.maxRetries || 3
    this.retryDelay = options.retryDelay || 1000
    this.concurrentUploads = options.concurrentUploads || 3
    
    // アップロードキュー
    this.uploadQueue = []
    this.activeUploads = new Set()
    this.isProcessing = false
    
    // フィードバック用: アップロード後もIndexedDBにチャンクを残す（自己確認再生用）
    this.keepChunksForPlayback = options.keepChunksForPlayback === true

    // イベントハンドラー
    this.onProgress = options.onProgress || (() => {})
    this.onComplete = options.onComplete || (() => {})
    this.onChunkUploaded = options.onChunkUploaded || (() => {})
    this.onError = options.onError || (() => {})
  }
  
  /**
   * チャンクアップロード開始
   */
  async uploadChunk(chunkData, chunkInfo) {
    const uploadId = `${chunkInfo.recordingId}_${chunkInfo.chunkIndex}`
    
    // 既にアップロード中の場合はキューに追加
    if (this.activeUploads.has(uploadId)) {
      console.log('⏳ アップロード中、キューに追加:', uploadId)
      this.uploadQueue.push({ chunkData, chunkInfo })
      return
    }
    
    try {
      this.activeUploads.add(uploadId)
      
      // 1. 署名付きURL取得
      const presignedUrl = await this.getPresignedUrl(chunkInfo)
      
      // 2. R2に直接アップロード
      await this.uploadToR2(chunkData, presignedUrl, chunkInfo)
      
      // 3. 成功時はIndexedDBを更新
      const chunkId = chunkInfo.recordingId + '_' + chunkInfo.chunkIndex
      await this.storage.markChunkAsUploaded(chunkId)

      // 4. アップロード成功後、チャンクを削除（ストレージ節約）。フィードバック用は残す
      if (!this.keepChunksForPlayback) {
        await this.storage.deleteUploadedChunk(chunkId)
      }

      console.log('✅ チャンクアップロード成功:', uploadId)
      
      // 5. 最後のチャンクの場合、バックエンドに録音完了通知
      if (chunkInfo.isLastChunk) {
        console.log('🎯 最後のチャンクアップロード完了 → バックエンド通知開始')
        try {
          const result = await this.notifyRecordingComplete(
            chunkInfo.recordingId,
            chunkInfo.userId,
            chunkInfo.day,
            chunkInfo.type,
            null, // totalChunks
            chunkInfo.codecType, // 🆕 codecType追加
            chunkInfo.recordingMode, // フィードバック録音フラグ
            chunkInfo.studentId, // フィードバック対象の生徒ID
            chunkInfo.submissionId, // 対象の提出ID
            chunkInfo.learnerId, // 兄弟対応: この提出が紐づく子
            chunkInfo.mediaKind, // 'audio' または 'video'（フィードバック動画用）
            chunkInfo.rotationDeg // 動画のみ: 講師が確定した回転角度（0/90/180/270）
          )
          console.log('✅ バックエンド通知完了 (最後のチャンク完了時)', result)
        } catch (error) {
          console.error('❌ バックエンド通知エラー (最後のチャンク完了時):', error)
          // エラーでも他の処理は続行
        }
      }
      
      // 6. アップロード完了コールバックを呼び出し
      this.onChunkUploaded(chunkInfo)
      
      // 7. 次のキューを処理
      this.processQueue()
      
    } catch (error) {
      console.error('❌ チャンクアップロードエラー:', error)
      
      // リトライ処理
      await this.handleUploadError(chunkData, chunkInfo, error)
      
    } finally {
      this.activeUploads.delete(uploadId)
    }
  }
  
  /**
   * 署名付きURL取得
   */
  async getPresignedUrl(chunkInfo) {
    // ネットワーク状態チェック
    if (!navigator.onLine) {
      throw new Error('オフライン状態です。ネットワーク接続を確認してください。')
    }
    
    // 必須パラメータの検証
    if (!chunkInfo.recordingId) {
      throw new Error('recordingIdが不足しています')
    }
    if (chunkInfo.chunkIndex === undefined || chunkInfo.chunkIndex === null) {
      throw new Error('chunkIndexが不足しています')
    }
    if (!chunkInfo.size || chunkInfo.size <= 0) {
      throw new Error('sizeが無効です')
    }
    
    const requestBody = {
      recordingId: String(chunkInfo.recordingId),
      chunkIndex: Number(chunkInfo.chunkIndex),
      mimeType: String(chunkInfo.mimeType || chunkInfo.type || 'audio/webm'),
      size: Number(chunkInfo.size),
      userId: String(chunkInfo.userId),
      day: Number(chunkInfo.day),
      type: String(chunkInfo.type),
      // フィードバック録音用パラメータ
      recordingMode: chunkInfo.recordingMode, // 'feedback' または undefined
      studentId: chunkInfo.studentId, // フィードバック対象の生徒ID
      submissionId: chunkInfo.submissionId, // 対象の提出ID
      mediaKind: chunkInfo.mediaKind || 'audio' // 'audio' または 'video'（フィードバック動画用）
    }
    
    console.log('🔍 署名付きURL取得リクエスト:', {
      url: `${this.apiBaseUrl}/api/presigned-url`,
      body: requestBody,
      chunkInfo: chunkInfo
    })
    
    const response = await fetch(`${this.apiBaseUrl}/api/presigned-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this.getAuthToken()}`
      },
      body: JSON.stringify(requestBody)
    })
    
    if (!response.ok) {
      // エラーレスポンスの詳細を取得
      let errorMessage = `署名付きURL取得失敗: ${response.status}`
      try {
        const errorData = await response.json()
        console.error('❌ バックエンドエラー詳細:', errorData)
        errorMessage = errorData.error || errorData.message || errorMessage
      } catch (e) {
        console.error('❌ エラーレスポンス解析失敗:', e)
      }
      
      if (response.status === 0) {
        throw new Error('サーバーに接続できません。ネットワーク接続を確認してください。')
      } else if (response.status >= 500) {
        throw new Error('サーバーエラーが発生しました。しばらく待ってから再試行してください。')
      } else if (response.status === 401) {
        throw new Error('認証エラーが発生しました。ログインし直してください。')
      } else {
        throw new Error(errorMessage)
      }
    }
    
    const data = await response.json()
    return data.presignedUrl
  }
  
  /**
   * R2に直接アップロード
   */
  async uploadToR2(chunkData, presignedUrl, chunkInfo) {
    console.log(`📤 R2アップロード開始: チャンク${chunkInfo.chunkIndex}`)
    console.log(`📤 データサイズ: ${chunkData.size || chunkData.byteLength} bytes`)
    console.log(`📤 Content-Type: ${chunkInfo.mimeType || chunkData.type}`)
    console.log(`📤 データタイプ: ${chunkData.constructor.name}`)
    
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': chunkInfo.mimeType || chunkData.type
      },
      body: chunkData
    })
    
    if (!response.ok) {
      console.error(`❌ R2アップロード失敗: ${response.status}`)
      console.error(`❌ レスポンス詳細:`, response)
      throw new Error(`R2アップロード失敗: ${response.status}`)
    }
    
    console.log(`✅ チャンクアップロード成功: チャンク${chunkInfo.chunkIndex} (${chunkData.size || chunkData.byteLength} bytes)`)
    
    // HEAD検証は一時的に無効化（CORS問題のため）
    // TODO: R2のCORS設定修正後に再有効化
    // await this.verifyUploadCompletion(presignedUrl, chunkData, chunkInfo)
    
    return response
  }
  
  /**
   * HEAD検証によるアップロード完了確認
   */
  async verifyUploadCompletion(presignedUrl, chunkData, chunkInfo) {
    try {
      // presigned URLからHEAD用URLを作成（クエリパラメータを除去）
      const headUrl = presignedUrl.split('?')[0]
      
      console.log(`🔍 HEAD検証開始: チャンク${chunkInfo.chunkIndex}`)
      
      const headResponse = await fetch(headUrl, {
        method: 'HEAD'
      })
      
      if (!headResponse.ok) {
        throw new Error(`HEAD検証失敗: ${headResponse.status}`)
      }
      
      const contentLength = parseInt(headResponse.headers.get('Content-Length') || '0')
      const expectedSize = chunkData.size || chunkData.byteLength
      
      console.log(`🔍 サイズ検証: 期待=${expectedSize}bytes, 実際=${contentLength}bytes`)
      
      if (contentLength !== expectedSize) {
        throw new Error(`サイズ不一致: 期待=${expectedSize}bytes, 実際=${contentLength}bytes`)
      }
      
      // 最低閾値チェック（1KB未満は異常）
      if (contentLength < 1024) {
        console.warn(`⚠️ 小さなチャンク: ${contentLength}bytes (チャンク${chunkInfo.chunkIndex})`)
      }
      
      console.log(`✅ HEAD検証完了: チャンク${chunkInfo.chunkIndex} (${contentLength}bytes)`)
      
    } catch (error) {
      console.error(`❌ HEAD検証エラー: チャンク${chunkInfo.chunkIndex}:`, error)
      throw new Error(`アップロード検証失敗: ${error.message}`)
    }
  }

  /**
   * アップロードエラー処理
   */
  async handleUploadError(chunkData, chunkInfo, error) {
    const chunkId = chunkInfo.recordingId + '_' + chunkInfo.chunkIndex
    
    // 試行回数更新
    const updatedChunk = await this.storage.updateUploadAttempts(chunkId, false)
    
    if (updatedChunk.uploadAttempts < this.maxRetries) {
      // リトライ可能
      console.log(`🔄 アップロードリトライ: ${updatedChunk.uploadAttempts}/${this.maxRetries}`, chunkId)
      
      // 指数バックオフで遅延
      const delay = this.retryDelay * Math.pow(2, updatedChunk.uploadAttempts - 1)
      setTimeout(() => {
        this.uploadChunk(chunkData, chunkInfo)
      }, delay)
      
    } else {
      // リトライ上限到達 - チャンクを失敗としてマーク
      console.error('❌ アップロードリトライ上限到達:', chunkId)
      
      // チャンクを失敗としてマーク（再取得を防ぐ）
      await this.storage.markChunkAsFailed(chunkId)
      
      this.onError({
        type: 'upload_failed',
        chunkId,
        chunkInfo,
        error: error.message
      })
    }
  }
  
  /**
   * アップロードキュー処理
   */
  async processQueue() {
    if (this.isProcessing || this.uploadQueue.length === 0) {
      return
    }
    
    this.isProcessing = true
    
    while (this.uploadQueue.length > 0 && this.activeUploads.size < this.concurrentUploads) {
      const { chunkData, chunkInfo } = this.uploadQueue.shift()
      this.uploadChunk(chunkData, chunkInfo)
    }
    
    this.isProcessing = false
  }
  
  /**
   * 未アップロードチャンクの再処理
   */
  async retryFailedUploads() {
    try {
      const unuploadedChunks = await this.storage.getUnuploadedChunks()
      
      console.log(`🔄 未アップロードチャンク再処理開始: ${unuploadedChunks.length}個`)
      
      for (const chunk of unuploadedChunks) {
        if (chunk.uploadAttempts < this.maxRetries) {
          this.uploadQueue.push({
            chunkData: chunk.data,
            chunkInfo: {
              recordingId: chunk.recordingId,
              chunkIndex: chunk.chunkIndex,
              timestamp: chunk.timestamp,
              size: chunk.size,
              type: chunk.mimeType,
              day: chunk.day,
              type: chunk.type,
              userId: chunk.userId
            }
          })
        }
      }
      
      await this.processQueue()
      
    } catch (error) {
      console.error('❌ 失敗チャンク再処理エラー:', error)
    }
  }
  
  /**
   * 録音完了時の最終処理
   */
  // 注意: この関数は使用されなくなりました
  // 最後のチャンクアップロード完了時に自動でバックエンド通知が行われます
  async finalizeRecording(recordingId, userId, day, type) {
    console.log('⚠️ finalizeRecording は非推奨: 最後のチャンクアップロード完了時に自動実行されます')
  }
  
  /**
   * 全アップロード完了待機
   */
  async waitForAllUploads(recordingId) {
    const maxWaitTime = 30000 // 30秒
    const checkInterval = 500 // 0.5秒（より頻繁にチェック）
    let waitTime = 0
    
    console.log(`⏳ 全アップロード完了待機開始: ${recordingId}`)
    
    while (waitTime < maxWaitTime) {
      const unuploadedChunks = await this.storage.getUnuploadedChunks()
      const recordingChunks = unuploadedChunks.filter(chunk => chunk.recordingId === recordingId)
      
      console.log(`⏳ 待機中: ${recordingChunks.length}個の未アップロードチャンク (${waitTime}ms経過)`)
      
      if (recordingChunks.length === 0) {
        console.log(`✅ 全アップロード完了確認: ${recordingId}`)
        // 追加の安全マージン
        await new Promise(resolve => setTimeout(resolve, 2000))
        console.log(`✅ 安全マージン完了: ${recordingId}`)
        return true
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval))
      waitTime += checkInterval
    }
    
    console.error(`❌ アップロード完了待機タイムアウト: ${recordingId}`)
    throw new Error('アップロード完了待機タイムアウト')
  }
  
  /**
   * 録音完了通知
   */
  async notifyRecordingComplete(recordingId, userId, day, type, totalChunks = null, codecType = null, recordingMode = null, studentId = null, submissionId = null, learnerId = null, mediaKind = null, rotationDeg = null) {
    const requestBody = {
      recordingId,
      userId,
      day,
      type
    }
    
    // totalChunksが指定されている場合のみ追加
    if (totalChunks !== null) {
      requestBody.totalChunks = totalChunks
    }
    
    // 🆕 codecType（webm/mp4）を追加
    if (codecType) {
      requestBody.codecType = codecType
    }
    
    // フィードバック録音用パラメータを追加
    if (recordingMode) {
      requestBody.recordingMode = recordingMode
    }
    if (studentId) {
      requestBody.studentId = studentId
    }
    if (submissionId) {
      requestBody.submissionId = submissionId
    }
    // 兄弟対応: この提出が紐づく子（learners.id）
    if (learnerId) {
      requestBody.learnerId = learnerId
    }
    // フィードバック動画用: 'audio' または 'video'
    if (mediaKind) {
      requestBody.mediaKind = mediaKind
    }
    // 動画のみ: 講師が「向きを直す」ボタンで確定した回転角度（0/90/180/270）
    if (rotationDeg !== null && rotationDeg !== undefined) {
      requestBody.rotationDeg = rotationDeg
    }

    console.log('🔍 録音完了通知リクエスト:', {
      url: `${this.apiBaseUrl}/api/recording-complete`,
      body: requestBody
    })
    
    const response = await fetch(`${this.apiBaseUrl}/api/recording-complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this.getAuthToken()}`
      },
      body: JSON.stringify(requestBody)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ 録音完了通知エラー詳細:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      })
      throw new Error(`録音完了通知失敗: ${response.status}`)
    }
    
    return response.json()
  }
  
  /**
   * 認証トークン取得
   */
  async getAuthToken() {
    // 既存のSupabaseクライアントを使用
    const { supabase } = await import('../supabaseClient.js')
    
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('認証トークンが見つかりません')
    }
    
    return session.access_token
  }
  
  /**
   * 状態取得
   */
  getStatus() {
    return {
      queueLength: this.uploadQueue.length,
      activeUploads: this.activeUploads.size,
      isProcessing: this.isProcessing
    }
  }
}
